import { t } from "@lingui/core/macro";
import { isNil } from "~utils/value";
import { uniqWith } from "lodash-es";
import { Selection } from "react-stately";
import { LockState, Point, PointType, PointValue } from "./point.model";

export function pointTypeFromNumber(type: number): PointType {
  return type in PointType ? (type as PointType) : PointType.None;
}

export function getPointTypeLabel(type: PointType) {
  const pointTypeText: Record<PointType, string> = {
    0: t`Unknown`,
    1: t`Indication`,
    2: t`DO point`,
    3: t`Measurement`,
    4: t`AO point`,
    5: t`Conversion table`,
    6: t`Time schedule`,
    7: t`Alarm point`,
    13: t`Control point`,
    17: t`Calendar`,
  };

  return pointTypeText[type] || t`Unknown`;
}

export function getIsPointErrorState(
  errorState: number | null | undefined,
): boolean {
  return !isNil(errorState) && errorState !== 0;
}

export function getPointLockStateLabel(lockState?: LockState | null) {
  const lockStateText: Record<LockState, string> = {
    0: t`Auto`,
    1: t`Program`,
    2: t`Manual`,
  };

  return lockStateText[lockState as LockState] || "-";
}

type PointValueLabel = Pick<Point, "type" | "unit"> & {
  value?: Pick<PointValue, "label" | "value">;
};
export function getPointValueLabel({ type, value, unit }: PointValueLabel) {
  // Some types of points don't have a value that can be displayed
  if (
    !value ||
    type === PointType.Calendar ||
    type === PointType.ConversionTable
  ) {
    return "-";
  }

  // If point has state text based label, use it
  if (value.label) {
    return value.label;
  }

  if (isNil(value.value)) {
    return "-";
  }

  // If point has a unit, append it to the value
  if (unit) {
    return `${value.value} ${unit}`;
  }

  return value.value;
}

export function formatPointValue({ value, decimals, unit, stateTexts }: Point) {
  if (!value) {
    return "-";
  }

  if (value.label) {
    return value.label;
  }

  if (isNil(value.value)) {
    return "-";
  }

  const label = stateTexts?.find(
    (stateText) => stateText.value === value.value,
  )?.label;
  if (label) {
    return label;
  }

  const formattedValue = isNil(decimals)
    ? value.value
    : value.value.toFixed(decimals);

  if (unit) {
    return `${formattedValue} ${unit}`;
  }

  return formattedValue.toString();
}

export function formatLimitValue(
  value: number | null,
  decimals: number | null,
  unit: string | null,
) {
  if (isNil(value)) {
    return "-";
  }

  const formattedValue = isNil(decimals) ? value : value.toFixed(decimals);

  if (unit) {
    return `${formattedValue} ${unit}`;
  }

  return formattedValue.toString();
}

export function selectedRows<T extends { id: number }>(
  newIds: Selection,
  selectedRows: T[],
  visibleRows?: T[],
) {
  // If the user presses the "select all" checkbox, just add all the selected points with the already
  // selected points and remove duplicates.
  if (newIds === "all") {
    const newPoints = [...selectedRows, ...(visibleRows || []).map((p) => p)];
    return uniqWith(newPoints, (a, b) => a.id === b.id);
  }

  // Handle when the user removes a point
  if (newIds.size < selectedRows.length) {
    // We have to do some magic here with the points when the user removes all points with the select all
    // checkbox. For some reason the received newIds will be a completely empty Set eventhough there would be points
    // outside of the view of the table
    const newPoints =
      newIds.size === 0
        ? selectedRows.filter((p) => !visibleRows?.some((o) => o.id === p.id))
        : selectedRows.filter((p) => newIds.has(p.id));
    return newPoints;
  }
  // Handle when the user adds a point
  else {
    const newPointId = [...newIds].filter(
      (id) => !selectedRows.some((p) => p.id === id),
    );
    const newPoint = visibleRows?.find((p) => p.id === newPointId[0]);
    const newPoints = [...selectedRows, ...(newPoint ? [newPoint] : [])];
    return newPoints;
  }
}

type HistoryTab = "table" | "visual";

export function getDefaultHistoryTab(point: Point): HistoryTab {
  return point.type === PointType.Alarm ||
    point.type === PointType.DI ||
    point.type === PointType.DO ||
    point.type === PointType.TimeSchedule
    ? "table"
    : "visual";
}
